import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  useTheme,
  Tooltip,
  alpha,
  Switch,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Block,
  AdminPanelSettings,
  ExpandMore,
  ContentCopy,
  Check,
  AutoAwesome,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { roleService, Role, CreateRoleData, UpdateRoleData } from '../../services/roleService';
import { permissionService } from '../../services/permissionService';
import { useAuth } from '../../context/AuthContext';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import { ToneChip } from '../common/StatusChip';
import {
  MODULE_FEATURE_MAP,
  MODULES_BY_CATEGORY,
  type ModuleCategory,
  type ModuleDef,
} from '../../constants/permissions';

// ---------------------------------------------------------------------------
// Local types & template presets
// ---------------------------------------------------------------------------

interface Permission {
  _id: string;
  name: string;
  module: string;
  action: 'read' | 'write';
  description?: string;
}

/**
 * Permission state keyed by module code. Each value is { read, write }.
 * Decoupled from Permission ObjectIds — we only need ObjectIds at save time
 * (when we re-map module/action to the matching Permission._id).
 */
type PermissionMap = Record<string, { read: boolean; write: boolean }>;

interface RoleTemplate {
  key: string;
  name: string;
  description: string;
  /** scopes granted by the template, e.g. 'results.read', 'enter_marks.write' */
  scopes: readonly string[];
}

const TEMPLATES: readonly RoleTemplate[] = [
  {
    key: 'class_teacher',
    name: 'Class Teacher',
    description: 'Owns a homeroom section — sees results, enters marks, takes attendance.',
    scopes: [
      'dashboard.read',
      'results.read', 'results.write',
      'enter_marks.read', 'enter_marks.write',
      'student_attendance.read', 'student_attendance.write',
      'student_progress.read', 'subject_performance.read', 'class_performance.read',
      'student_reports.read', 'student_reports.write',
    ],
  },
  {
    key: 'subject_teacher',
    name: 'Subject Teacher',
    description: 'Enters marks for the subjects they teach. Views results read-only.',
    scopes: [
      'dashboard.read',
      'enter_marks.read', 'enter_marks.write',
      'results.read',
      'student_progress.read', 'subject_performance.read',
    ],
  },
  {
    key: 'receptionist',
    name: 'Receptionist',
    description: 'Admissions, events, letters, basic student directory.',
    scopes: [
      'dashboard.read',
      'students.read',
      'admissions.read', 'admissions.write',
      'events.read', 'events.write',
      'letters.read', 'letters.write',
      'notifications.read', 'notifications.write',
    ],
  },
  {
    key: 'bursar',
    name: 'Bursar',
    description: 'Manages fees and expenses; views student directory for billing.',
    scopes: [
      'dashboard.read',
      'students.read',
      'expenses.read', 'expenses.write',
      'expense_approval.read', 'expense_approval.write',
      'billing.read',
    ],
  },
  {
    key: 'admissions_officer',
    name: 'Admissions Officer',
    description: 'Runs the admissions pipeline from applications through enrollment: offers, waitlist and concession requests. Exceptions and concessions still need an approver.',
    scopes: [
      'dashboard.read',
      'admissions.read', 'admissions.write',
      'admissions_settings.read', 'admissions_settings.write',
      'admissions_decisions.read', 'admissions_decisions.write',
      'admissions_concessions.read', 'admissions_concessions.write',
      'students.read', 'students.write',
      'enrollments.read', 'enrollments.write',
    ],
  },
  {
    key: 'admissions_approver',
    name: 'Admissions Approver',
    description: 'Principal-level sign-off: approves decision exceptions and concessions, can override fee steps and see sensitive form fields. Cannot approve their own requests.',
    scopes: [
      'dashboard.read',
      'admissions.read', 'admissions.write',
      'admissions_settings.read',
      'admissions_decisions.read', 'admissions_decisions.write',
      'admissions_decisions_approve.read', 'admissions_decisions_approve.write',
      'admissions_concessions.read',
      'admissions_concessions_approve.read', 'admissions_concessions_approve.write',
      'admissions_fee_override.read', 'admissions_fee_override.write',
      'admissions_read_sensitive.read',
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const emptyMap = (modules: readonly ModuleDef[]): PermissionMap =>
  Object.fromEntries(modules.map(m => [m.code, { read: false, write: false }]));

const mapFromScopes = (modules: readonly ModuleDef[], scopes: readonly string[]): PermissionMap => {
  const base = emptyMap(modules);
  const set = new Set(scopes);
  for (const m of modules) {
    base[m.code] = {
      read:  set.has(`${m.code}.read`)  || set.has(`${m.code}.write`), // write implies read
      write: set.has(`${m.code}.write`),
    };
  }
  return base;
};

const scopesFromMap = (modules: readonly ModuleDef[], map: PermissionMap): string[] => {
  const out: string[] = [];
  for (const m of modules) {
    const cell = map[m.code];
    if (cell?.read)  out.push(`${m.code}.read`);
    if (cell?.write) out.push(`${m.code}.write`);
  }
  return out;
};

const summarize = (map: PermissionMap) => {
  const modules = Object.keys(map).filter(k => map[k].read || map[k].write).length;
  const reads = Object.values(map).filter(v => v.read).length;
  const writes = Object.values(map).filter(v => v.write).length;
  return { modules, reads, writes };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RolesSettingsSimplified: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user, decodedToken } = useAuth();

  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Form state
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [permMap, setPermMap] = useState<PermissionMap>({});
  const [templateAnchor, setTemplateAnchor] = useState<null | HTMLElement>(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewCopied, setPreviewCopied] = useState(false);

  const canManageRoles = !!user?.isSystemAdmin || decodedToken?.isSchoolAdmin === true;

  // Subscription-aware module list, then bucketed by category for the form.
  const visibleGroups = useMemo(() => {
    const features = decodedToken?.features || [];
    const hasAll = !!decodedToken?.isSystemAdmin || features.includes('all');
    return MODULES_BY_CATEGORY
      .map(g => ({
        category: g.category,
        modules: g.modules.filter(m => {
          if (hasAll) return true;
          const req = MODULE_FEATURE_MAP[m.code];
          return req === 'free' || features.includes(req);
        }),
      }))
      .filter(g => g.modules.length > 0);
  }, [decodedToken]);

  const allVisibleModules = useMemo(
    () => visibleGroups.flatMap(g => g.modules),
    [visibleGroups]
  );

  // Permission code → ObjectId lookup. Computed once after permissions load.
  const permIdByScope = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of availablePermissions) m.set(`${p.module}.${p.action}`, p._id);
    return m;
  }, [availablePermissions]);

  // ── Data loading ──
  const loadRoles = useCallback(async () => {
    try {
      const data = await roleService.getRoles();
      setRoles(data || []);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Error loading roles', { variant: 'error' });
    } finally {
      setRolesLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadPermissions = useCallback(async () => {
    try {
      const response = await permissionService.getPermissions({ limit: 1000 });
      if (response?.permissions) {
        setAvailablePermissions(response.permissions as unknown as Permission[]);
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Error loading permissions', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (canManageRoles) {
      loadRoles();
      loadPermissions();
    }
  }, [canManageRoles, loadRoles, loadPermissions]);

  // ── Dialog open / close ──
  const handleOpenDialog = useCallback((role?: Role) => {
    if (role) {
      setEditingRole(role);
      setRoleName(role.name);
      setRoleDescription(role.description || '');
      // Convert role.permissions (populated docs) into scope strings, then to map.
      const scopes = role.permissions
        .map(p => (typeof p === 'string' ? null : `${p.module}.${p.action}`))
        .filter((s): s is string => !!s);
      setPermMap(mapFromScopes(allVisibleModules, scopes));
    } else {
      setEditingRole(null);
      setRoleName('');
      setRoleDescription('');
      setPermMap(emptyMap(allVisibleModules));
    }
    setDialogOpen(true);
  }, [allVisibleModules]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setPermMap({});
  };

  // ── Toggles ──
  const toggleRead = (code: string) => {
    setPermMap(prev => {
      const cell = prev[code] ?? { read: false, write: false };
      const nextRead = !cell.read;
      // Turning read OFF must also turn write OFF (write implies read).
      return { ...prev, [code]: { read: nextRead, write: nextRead ? cell.write : false } };
    });
  };

  const toggleWrite = (code: string) => {
    setPermMap(prev => {
      const cell = prev[code] ?? { read: false, write: false };
      const nextWrite = !cell.write;
      // Turning write ON auto-turns read ON.
      return { ...prev, [code]: { read: nextWrite || cell.read, write: nextWrite } };
    });
  };

  const setCategoryAll = (category: ModuleCategory, action: 'read' | 'write', on: boolean) => {
    const grp = visibleGroups.find(g => g.category === category);
    if (!grp) return;
    setPermMap(prev => {
      const next = { ...prev };
      for (const m of grp.modules) {
        const cell = next[m.code] ?? { read: false, write: false };
        if (action === 'read') {
          next[m.code] = { read: on, write: on ? cell.write : false };
        } else {
          next[m.code] = { read: on ? true : cell.read, write: on };
        }
      }
      return next;
    });
  };

  const setAllAll = (action: 'read' | 'write', on: boolean) => {
    setPermMap(prev => {
      const next = { ...prev };
      for (const m of allVisibleModules) {
        const cell = next[m.code] ?? { read: false, write: false };
        if (action === 'read') {
          next[m.code] = { read: on, write: on ? cell.write : false };
        } else {
          next[m.code] = { read: on ? true : cell.read, write: on };
        }
      }
      return next;
    });
  };

  // ── Templates ──
  const applyTemplate = (tpl: RoleTemplate) => {
    setPermMap(mapFromScopes(allVisibleModules, tpl.scopes));
    setTemplateAnchor(null);
    enqueueSnackbar(`Applied "${tpl.name}" template — review and save.`, { variant: 'info' });
  };

  // ── Save ──
  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      enqueueSnackbar('Role name is required', { variant: 'error' });
      return;
    }

    const scopes = scopesFromMap(allVisibleModules, permMap);

    if (scopes.length > 0 && permIdByScope.size === 0) {
      enqueueSnackbar(
        'Permission catalog is empty. Restart the backend (the seeder runs on boot) and reload this page.',
        { variant: 'error', persist: true }
      );
      return;
    }

    // Hard validation — if the user toggled a scope but the catalog has no
    // matching Permission doc, the save would silently drop the scope and
    // the role would look correct in the form yet have no effect at runtime.
    // Refuse to save until the seeder catches up.
    const missing = scopes.filter(s => !permIdByScope.has(s));
    if (missing.length > 0) {
      enqueueSnackbar(
        `Permission catalog is out of date. Missing docs for: ${missing.join(', ')}. ` +
          'Restart the backend (the seeder runs on boot) and reload this page.',
        { variant: 'error', persist: true }
      );
      // eslint-disable-next-line no-console
      console.error('Missing permission catalog entries:', { missing, catalog: Array.from(permIdByScope.keys()) });
      return;
    }

    const permissionIds = scopes.map(s => permIdByScope.get(s)!);

    try {
      if (editingRole) {
        const payload: UpdateRoleData = {
          name: roleName.trim(),
          description: roleDescription.trim(),
          permissions: permissionIds,
        };
        await roleService.updateRole(editingRole._id, payload);
        enqueueSnackbar('Role updated', { variant: 'success' });
      } else {
        const payload: CreateRoleData = {
          name: roleName.trim(),
          description: roleDescription.trim(),
          permissions: permissionIds,
        };
        await roleService.createRole(payload);
        enqueueSnackbar('Role created', { variant: 'success' });
      }
      handleCloseDialog();
      await loadRoles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Error saving role', { variant: 'error' });
    }
  };

  // ── Live preview ──
  const previewScopes = useMemo(
    () => scopesFromMap(allVisibleModules, permMap),
    [allVisibleModules, permMap]
  );
  const stats = useMemo(() => summarize(permMap), [permMap]);

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(previewScopes.join('\n'));
      setPreviewCopied(true);
      setTimeout(() => setPreviewCopied(false), 1500);
    } catch { /* ignore */ }
  };

  if (!canManageRoles) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Block sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Only System Admin and School Admin can manage user roles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SettingsList
        rows={roles}
        loading={rolesLoading}
        rowKey={r => r._id}
        name={r => r.name}
        subtitle={r => r.description || 'No description'}
        leading={() => <SettingsTile color={theme.palette.primary.main}><AdminPanelSettings /></SettingsTile>}
        meta={r => (
          <>
            {r.isSystemRole && <ToneChip label="System" tone="highlight" />}
            <ToneChip
              label={`${r.permissions.length} permission${r.permissions.length === 1 ? '' : 's'}`}
              tone="neutral"
            />
          </>
        )}
        searchText={r => `${r.name} ${r.description ?? ''}`}
        canWrite={canManageRoles}
        onEdit={r => handleOpenDialog(r)}
        // System roles are editable only when they are the teacher role.
        editBlocked={r => (r.isSystemRole && !r.isTeacher ? "System roles can't be edited" : null)}
        remove={{
          run: r => roleService.deleteRole(r._id),
          noun: 'role',
          blocked: r => (r.isSystemRole ? "System roles can't be deleted" : null),
          consequence: r => `${r.name} will be removed. This can't be undone. A role still assigned to users can't be deleted; move those users to another role first.`,
        }}
        reload={loadRoles}
        empty={{
          icon: <AdminPanelSettings />,
          title: 'No roles yet',
          description: 'Roles decide what each staff member can see and change. Add one to get started.',
        }}
        toolbar={
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Add role
          </Button>
        }
      />

      {/* ─── Role create/edit dialog ─────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <AdminPanelSettings color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {editingRole ? 'Edit Role' : 'Create New Role'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure what this role can see and change across the system
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {/* Name + description */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Role name"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                fullWidth
                required
                disabled={editingRole?.isSystemRole === true}
                placeholder="e.g. Class Teacher, Bursar"
                helperText={editingRole?.isSystemRole ? 'System role name cannot be changed' : ' '}
              />
              <TextField
                label="Description"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                fullWidth
                placeholder="What this role is for"
                helperText=" "
              />
            </Stack>

            {/* Toolbar: templates + summary chips */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Button
                size="small"
                startIcon={<AutoAwesome />}
                onClick={(e) => setTemplateAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                Start from a template
              </Button>
              <Menu
                anchorEl={templateAnchor}
                open={!!templateAnchor}
                onClose={() => setTemplateAnchor(null)}
                slotProps={{ paper: { sx: { mt: 0.5, minWidth: 320, borderRadius: 2 } } }}
              >
                {TEMPLATES.map(tpl => (
                  <MenuItem key={tpl.key} onClick={() => applyTemplate(tpl)} sx={{ py: 1.25, gap: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{tpl.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tpl.description}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip size="small" variant="outlined" color="primary"
                      label={`${stats.modules} module${stats.modules === 1 ? '' : 's'}`} />
                <Chip size="small" variant="outlined" color="info"  label={`${stats.reads} read`} />
                <Chip size="small" variant="outlined" color="success" label={`${stats.writes} write`} />
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Button size="small" onClick={() => setAllAll('read', true)} sx={{ textTransform: 'none' }}>
                  All read
                </Button>
                <Button size="small" onClick={() => setAllAll('write', true)} sx={{ textTransform: 'none' }}>
                  All write
                </Button>
                <Button size="small" onClick={() => { setAllAll('read', false); setAllAll('write', false); }} sx={{ textTransform: 'none' }}>
                  Clear
                </Button>
              </Stack>
            </Stack>

            {/* Grouped permissions */}
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              {visibleGroups.map((g) => {
                const groupReads  = g.modules.filter(m => permMap[m.code]?.read).length;
                const groupWrites = g.modules.filter(m => permMap[m.code]?.write).length;
                const total = g.modules.length;
                const allRead  = groupReads  === total;
                const allWrite = groupWrites === total;
                // Default open if Settings (granular intent) or any toggle is on.
                const defaultExpanded = g.category === 'Settings' || groupReads > 0;
                return (
                  <Accordion
                    key={g.category}
                    defaultExpanded={defaultExpanded}
                    elevation={0}
                    disableGutters
                    square
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:before': { display: 'none' },
                      '&:last-of-type': { borderBottom: 0 },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        bgcolor: alpha(theme.palette.text.primary, 0.025),
                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                        {g.category}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${groupReads}/${total} read · ${groupWrites}/${total} write`}
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: groupReads + groupWrites > 0
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.text.primary, 0.06),
                          color: groupReads + groupWrites > 0
                            ? theme.palette.primary.dark
                            : 'text.secondary',
                          fontWeight: 600,
                        }}
                      />
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Per-category master toggles */}
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                          px: 2,
                          py: 1,
                          bgcolor: alpha(theme.palette.text.primary, 0.015),
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                          Toggle the whole {g.category} category
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">Read</Typography>
                          <Switch
                            size="small"
                            color="info"
                            checked={allRead}
                            onChange={() => setCategoryAll(g.category, 'read', !allRead)}
                          />
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">Write</Typography>
                          <Switch
                            size="small"
                            color="success"
                            checked={allWrite}
                            onChange={() => setCategoryAll(g.category, 'write', !allWrite)}
                          />
                        </Stack>
                      </Stack>

                      {/* Module rows */}
                      <Stack divider={<Divider />}>
                        {g.modules.map((m) => {
                          const cell = permMap[m.code] ?? { read: false, write: false };
                          return (
                            <Box
                              key={m.code}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto auto',
                                alignItems: 'center',
                                gap: 2,
                                px: 2,
                                py: 1.25,
                                bgcolor: cell.read || cell.write
                                  ? alpha(theme.palette.primary.main, 0.035)
                                  : 'transparent',
                                transition: 'background-color 150ms ease',
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography sx={{ fontSize: '1.1rem' }}>{m.icon}</Typography>
                                  <Typography variant="body2" fontWeight={600} noWrap>
                                    {m.name}
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5, display: 'block' }}>
                                  {m.description}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 96, justifyContent: 'flex-end' }}>
                                <Typography variant="caption" color="text.secondary">Read</Typography>
                                <Switch
                                  size="small"
                                  color="info"
                                  checked={cell.read}
                                  onChange={() => toggleRead(m.code)}
                                />
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 96, justifyContent: 'flex-end' }}>
                                <Typography variant="caption" color="text.secondary">Write</Typography>
                                <Switch
                                  size="small"
                                  color="success"
                                  checked={cell.write}
                                  onChange={() => toggleWrite(m.code)}
                                />
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>

            {/* Live preview pane */}
            <Accordion
              expanded={previewExpanded}
              onChange={(_, expanded) => setPreviewExpanded(expanded)}
              elevation={0}
              square
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: alpha(theme.palette.text.primary, 0.025),
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
                  Effective scopes ({previewScopes.length})
                </Typography>
                <Tooltip title={previewCopied ? 'Copied' : 'Copy to clipboard'}>
                  <span onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={copyPreview}
                      disabled={previewScopes.length === 0}
                    >
                      {previewCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails>
                {previewScopes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No scopes selected. The role will have nothing it can do.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {previewScopes.map(s => (
                      <Chip
                        key={s}
                        size="small"
                        label={s}
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          color: 'text.primary',
                          fontFamily: 'monospace',
                        }}
                      />
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveRole}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {editingRole ? 'Save changes' : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default RolesSettingsSimplified;
